import { t } from "@lingui/macro";
import {
  createResumeDataFromDraft,
  draftHasExperience,
  emptyResumeDataForUser,
  type ResumeDraft,
  seedTypicalWork,
} from "@reactive-resume/schema";
import { Button } from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { Icon } from "@/client/components/icon";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { WelcomeOverlay } from "@/client/components/welcome-overlay";
import { toast } from "@/client/hooks/use-toast";
import { markOnboardingStartedAt, trackProductEvent, withOnboardingTiming } from "@/client/libs/analytics";
import { builderPath, uniqueResumeSlug } from "@/client/libs/resume-identity";
import { useImportResume } from "@/client/services/resume/import";
import { useResumes } from "@/client/services/resume/resumes";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";

import { Composer } from "./composer";
import { parsePlainTextToDraft } from "./parse-plain-text";
import { ResumePreview } from "./preview";
import { type IntakeMethod } from "./types";

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: userLoading } = useUser();
  const { resumes, loading: resumesLoading } = useResumes();
  const { importResume, loading: importLoading } = useImportResume();
  const { open: openImport } = useDialog("import");

  const [draft, setDraft] = useState<ResumeDraft>({});
  const [reading, setReading] = useState(false);
  const [writingPreview, setWritingPreview] = useState(false);
  const [enrich, setEnrich] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);

  const welcomeBootRef = useRef(false);
  const startedRef = useRef(false);
  const hadResumesOnMount = useRef<boolean | null>(null);
  const [welcomeActive, setWelcomeActive] = useState(false);
  const [welcomeName, setWelcomeName] = useState<string | undefined>(undefined);

  const firstName = useMemo(() => user?.name?.trim().split(/\s+/)[0], [user?.name]);
  const fullName = user?.name?.trim();
  const fallbackTitle = fullName ? t`${fullName}'s resume` : t`My resume`;
  const isAdditional = searchParams.get("new") === "1";

  useEffect(() => {
    if (welcomeBootRef.current) return;
    welcomeBootRef.current = true;
    if (searchParams.get("welcome") !== "1") return;
    setWelcomeName(searchParams.get("name") ?? firstName);
    setWelcomeActive(true);
    setSearchParams(
      (prev) => {
        prev.delete("welcome");
        prev.delete("name");
        return prev;
      },
      { replace: true },
    );
  }, [firstName, searchParams, setSearchParams]);

  useEffect(() => {
    if (startedRef.current || userLoading || resumesLoading) return;
    if ((resumes?.length ?? 0) > 0 && !isAdditional) return;
    startedRef.current = true;
    markOnboardingStartedAt();
    trackProductEvent("onboarding_started", {
      user_id: user?.id ?? null,
      is_additional: isAdditional || (resumes?.length ?? 0) > 0,
    });
  }, [isAdditional, resumes, resumesLoading, user?.id, userLoading]);

  useEffect(() => {
    if (!user?.name) return;
    setDraft((current) => {
      if (current.basics?.name) return current;
      return {
        ...current,
        basics: { ...current.basics, name: user.name, email: user.email ?? "" },
      };
    });
  }, [user?.email, user?.name]);

  const setPreviewDraft = useCallback((next: ResumeDraft, isReading = false) => {
    setDraft(next);
    setReading(isReading);
  }, []);

  const openBuilder = useCallback(
    (resumeId: string, method: IntakeMethod, hasExperience: boolean) => {
      trackProductEvent("intake_completed", withOnboardingTiming({ method, has_experience: hasExperience }));
      trackProductEvent("draft_opened_builder", { method });
      navigate(builderPath(resumeId, true), { replace: true });
    },
    [navigate],
  );

  const createBlank = useCallback(
    async (method: IntakeMethod = "blank") => {
      setCreating(true);
      try {
        const data = emptyResumeDataForUser({
          name: user?.name,
          email: user?.email,
          picture: user?.picture,
        });
        const resume = await importResume({
          title: fallbackTitle,
          slug: uniqueResumeSlug(fallbackTitle),
          visibility: "private",
          data,
        });
        openBuilder(resume.id, method, false);
      } catch (error) {
        setCreating(false);
        trackProductEvent("intake_failed", { method, reason: "save_error" });
        toast({
          variant: "error",
          title: t`Couldn't create your resume`,
          description: (error as Error).message,
        });
      }
    },
    [fallbackTitle, importResume, openBuilder, user?.email, user?.name, user?.picture],
  );

  const createFromDraft = useCallback(
    async (next: ResumeDraft, method: IntakeMethod) => {
      setCreating(true);
      setDraft(next);
      try {
        const data = createResumeDataFromDraft(next, {
          name: user?.name,
          email: user?.email,
          picture: user?.picture,
        });
        const resume = await importResume({
          title: fallbackTitle,
          slug: uniqueResumeSlug(fallbackTitle),
          visibility: "private",
          data,
        });
        openBuilder(resume.id, method, draftHasExperience(next));
      } catch (error) {
        setCreating(false);
        trackProductEvent("intake_failed", { method, reason: "save_error" });
        toast({
          variant: "error",
          title: t`Couldn't save your draft`,
          description: (error as Error).message,
        });
      }
    },
    [fallbackTitle, importResume, openBuilder, user?.email, user?.name, user?.picture],
  );

  const onComposeSubmit = async (text: string, previewDraft?: ResumeDraft) => {
    setPasteError(null);
    const fallback = enrich
      ? seedTypicalWork(parsePlainTextToDraft(text, { name: user?.name, email: user?.email }))
      : parsePlainTextToDraft(text, { name: user?.name, email: user?.email });
    const next = previewDraft ?? fallback;

    try {
      await createFromDraft(
        {
          ...next,
          basics: { name: user?.name, email: user?.email, ...next.basics },
        },
        "compose",
      );
    } catch {
      setCreating(false);
      setPasteError(t`We couldn't save just now. Your notes are still here. Try Continue again.`);
    }
  };

  if (userLoading || resumesLoading) {
    return <div className="min-h-screen bg-background" />;
  }

  if (user && !user.planTier) {
    const next = new URLSearchParams();
    if (searchParams.get("welcome") === "1") next.set("welcome", "1");
    const name = searchParams.get("name");
    if (name) next.set("name", name);
    const qs = next.toString();
    return <Navigate to={qs ? `/dashboard/resumes?${qs}` : "/dashboard/resumes"} replace />;
  }

  if (hadResumesOnMount.current === null) {
    hadResumesOnMount.current = (resumes?.length ?? 0) > 0 && !isAdditional;
  }

  if (hadResumesOnMount.current) {
    return <Navigate to="/dashboard/resumes" replace />;
  }

  const busy = creating || importLoading;

  return (
    <div
      className="flex h-screen w-full flex-col overflow-hidden bg-background"
      style={{ height: "100dvh", maxHeight: "100dvh" }}
    >
      <Helmet>
        <title>
          {t`Build your resume`} - {t`CareerBldr`}
        </title>
      </Helmet>

      {welcomeActive && (
        <WelcomeOverlay
          name={welcomeName}
          durationMs={1200}
          onDone={() => setWelcomeActive(false)}
        />
      )}

      <header className="flex shrink-0 items-center justify-between px-5 py-2.5 sm:px-7">
        <Link to="/" className="flex items-center gap-2">
          <Icon size={22} />
          <span className="text-sm font-semibold tracking-tight">{t`CareerBldr`}</span>
        </Link>
        <div className="flex items-center gap-1">
          {isAdditional && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard/resumes">{t`Resumes`}</Link>
            </Button>
          )}
          <ThemeSwitch size={16} />
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <section className="order-2 flex min-h-0 flex-1 flex-col overflow-hidden lg:order-1 lg:w-[42%] lg:flex-none">
          <Composer
            name={user?.name}
            email={user?.email}
            firstName={firstName}
            enrich={enrich}
            loading={busy}
            error={pasteError}
            writing={writingPreview}
            onEnrichChange={(value) => {
              setEnrich(value);
              trackProductEvent("intake_enrich_toggled", { enabled: value });
            }}
            onStarted={() => trackProductEvent("intake_method_selected", { method: "compose" })}
            onImport={() => {
              trackProductEvent("intake_method_selected", { method: "import" });
              openImport("create");
            }}
            onSkip={() => {
              trackProductEvent("intake_method_selected", { method: "blank" });
              void createBlank("blank");
            }}
            onDraftChange={setPreviewDraft}
            onWritingChange={setWritingPreview}
            onSubmit={(text, previewDraft) => onComposeSubmit(text, previewDraft)}
          />
        </section>

        <aside className="isolate order-1 flex h-[28vh] shrink-0 flex-col overflow-hidden border-b border-border/60 bg-secondary/40 p-3 sm:p-4 lg:order-2 lg:h-auto lg:min-h-0 lg:flex-1 lg:border-b-0 lg:border-l lg:p-5">
          <ResumePreview
            draft={draft}
            fallbackName={user?.name}
            reading={reading && !busy}
            writing={writingPreview}
          />
        </aside>
      </main>

      <AnimatePresence>
        {busy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          >
            <div className="space-y-3 text-center">
              <div className="mx-auto h-1 w-40 overflow-hidden rounded-full bg-secondary">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: "8%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.4, ease: "easeInOut" }}
                />
              </div>
              <p className="text-muted-foreground text-sm">{t`Opening your resume…`}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
